import { composeQuery, Query } from '../models/query';
import {
  DataResponse,
  Feature,
  PageableResponse,
  Subscription,
} from '../models';
import { Axios, ceV2Axios } from './axios';

export const toggleAutoRenew = async (recurlySubscriptionId: number) => (await ceV2Axios.put<boolean>(`/subscriptions/toggle-auto-renewal/${recurlySubscriptionId}`)).data;

export const getSubscriptions = async (...criteria: Query<Subscription>[]) => (
  await ceV2Axios.get<PageableResponse<Subscription>>('/subscriptions', {
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
    },
    params: composeQuery<Subscription>(...criteria),
    data: {},
  })
).data.data;

/**
 * The BE's own new-account verdict — `SubscriptionService.isNewAccount`, the SAME function that
 * guards a True Bundle purchase and the starter package (Decision 6). The FE must never
 * re-implement it: the rule ("no Recurly subscription in the last 6 months, expired ones
 * included, monthly ones skipped when `checkMonthlyUserEnabled` is on") lives on the server.
 *
 * v1 endpoint: `GET {REACT_APP_API_URL}/subscriptions/is-access-starter-package?account_id=`,
 * i.e. the default `Axios` instance, whose baseURL already carries the `/v1` segment and whose
 * Authorization header is set alongside the v2 one (`setAuthorizationHeader`). It answers a RAW
 * boolean, not a DataResponse. `errorPresentation: 'caller'` keeps a failure out of the global
 * error snackbar: the caller decides what to do (here: hide the offer).
 */
export const isAccessStarterPackage = async (accountId: number): Promise<boolean> => (
  await Axios.get<boolean>('/subscriptions/is-access-starter-package', {
    headers: { 'Cache-Control': 'no-cache' },
    params: { account_id: accountId },
    errorPresentation: 'caller',
  })
).data;

export const getKseEcomEligible = async () => (await ceV2Axios.get<DataResponse<boolean>>('/subscriptions/kse-ecom-eligible')).data.data;

export type EcomEligibility = Partial<Record<Feature, boolean>>;

/**
 * Per-feature eCom channel eligibility of the current account, keyed by the BE feature enum
 * NAME (which equals the FE `Feature` value for every ECOM_FEATURES member). Eligible = the ecom
 * plan pair is seeded AND the account holds no active legacy subscription of that feature.
 */
export const getEcomEligibility = async (): Promise<EcomEligibility> => (
  (await ceV2Axios.get<DataResponse<Record<string, boolean>>>('/subscriptions/ecom-eligible'))
    .data.data ?? {}
) as EcomEligibility;
